/**
 * ActivityBrowser — sessions ↔ requests ↔ trace spans.
 *
 * Three columns, lazy-fetched:
 *   1. Sessions list (seeded from RSC, refresh + agent filter)
 *   2. Session detail = conversation timeline + requests table
 *   3. Selected request = trace span tree (click a span for attributes)
 *
 * URL state: `?agent=…&session=…&request=…` sync both ways so any view
 * is shareable as a link. We use `router.replace` for the URL writes
 * to avoid polluting back/forward history with every selection click.
 *
 * Everything is read-only and tenant-scoped via the gateway. Failed
 * fetches stay local to the pane that triggered them so a span 500
 * doesn't blank out the whole console.
 */

import { ChevronDown, ChevronRight, Link as LinkIcon, RefreshCw } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";

import { EmptyState, ErrorBanner } from "@/components/empty-state";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { api } from "@/lib/api";
import type {
  ActivityListEnvelope,
  ActivityMessage,
  ActivityRequest,
  ActivitySession,
  ActivitySessionDetail,
  ActivitySpan,
} from "@/lib/types";

interface ActivityBrowserProps {
  initial: {
    sessions: ActivitySession[];
    agents: { id: string; name: string }[];
  };
}

export function ActivityBrowser({ initial }: ActivityBrowserProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const pathname = location.pathname;
  const [searchParams] = useSearchParams();

  // Hydrate initial selection from the URL so deep links work on first paint.
  const initialAgentParam = searchParams.get("agent") ?? "";
  const initialSessionParam = searchParams.get("session");
  const initialRequestParam = searchParams.get("request");

  const [sessions, setSessions] = useState<ActivitySession[]>(initial.sessions);
  const [agentFilter, setAgentFilter] = useState<string>(initialAgentParam);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(
    initialSessionParam ?? initial.sessions[0]?.id ?? null,
  );
  const [selectedRequestId, setSelectedRequestId] = useState<string | null>(initialRequestParam);

  // Avoid pushing the URL on the very first render (we already hydrated
  // from the URL, no need to rewrite it back).
  const skipSyncRef = useRef(true);
  useEffect(() => {
    if (skipSyncRef.current) {
      skipSyncRef.current = false;
      return;
    }
    const params = new URLSearchParams();
    if (agentFilter) params.set("agent", agentFilter);
    if (selectedSessionId) params.set("session", selectedSessionId);
    if (selectedRequestId) params.set("request", selectedRequestId);
    const qs = params.toString();
    navigate(qs ? `${pathname}?${qs}` : (pathname ?? "/evaluations"), {
      replace: true,
      preventScrollReset: true,
    });
  }, [agentFilter, selectedSessionId, selectedRequestId, pathname, navigate]);

  // If the deep-linked agent filter is set, reload to apply it. Sessions
  // already contain the row; we just need to reflect the filter. This
  // runs exactly once on mount.
  // biome-ignore lint/correctness/useExhaustiveDependencies: hydration-only
  useEffect(() => {
    if (initialAgentParam) void refreshSessionsImpl(initialAgentParam);
  }, []);

  const agentNamesById = useMemo(() => {
    const m = new Map<string, string>();
    for (const a of initial.agents) m.set(a.id, a.name);
    return m;
  }, [initial.agents]);

  const refreshSessionsImpl = useCallback(async (filter: string) => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ limit: "100" });
      if (filter) params.set("agent_id", filter);
      const data = await api<ActivityListEnvelope<ActivitySession>>(
        `/activity/sessions?${params.toString()}`,
      );
      setSessions(data.data ?? []);
      // We deliberately do NOT clobber the selected session here: if the
      // user reloaded the page on a deep link (`?session=…`) we want to
      // keep that selection even when the session list is re-fetched.
      // Only clear if the selected one is no longer present AND there is
      // no deep-link selection at all.
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, []);
  const refreshSessions = refreshSessionsImpl;

  const copyLink = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      toast.success("Link copied");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to copy link");
    }
  }, []);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <Select
          value={agentFilter || "_all"}
          onValueChange={(v) => {
            const next = v === "_all" ? "" : v;
            setAgentFilter(next);
            void refreshSessions(next);
          }}
        >
          <SelectTrigger className="w-[260px]">
            <SelectValue placeholder="Filter by agent" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="_all">All agents</SelectItem>
            {initial.agents.map((a) => (
              <SelectItem key={a.id} value={a.id}>
                {a.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => void refreshSessions(agentFilter)}
          aria-label="Refresh sessions"
        >
          <RefreshCw />
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => void copyLink()}
          aria-label="Copy shareable link"
          // Only useful once the user has actually narrowed down to
          // something — link to the bare /evaluations is the same as no
          // link at all.
          disabled={!selectedSessionId && !agentFilter}
        >
          <LinkIcon />
        </Button>
        <span className="ml-auto text-xs text-muted-foreground">
          {sessions.length} session{sessions.length === 1 ? "" : "s"}
        </span>
      </div>

      {error ? <ErrorBanner>{error}</ErrorBanner> : null}

      <div className="grid grid-cols-1 gap-3 lg:grid-cols-[280px_minmax(0,1fr)]">
        {/* Sessions list */}
        <div className="rounded-lg border border-border bg-card/50">
          <div className="max-h-[60vh] overflow-y-auto">
            {loading ? (
              <div className="space-y-2 p-3">
                <Skeleton className="h-5 w-40" />
                <Skeleton className="h-5 w-52" />
              </div>
            ) : sessions.length === 0 ? (
              <EmptyState className="m-3">No sessions yet — kick off a chat run.</EmptyState>
            ) : (
              <ul className="divide-y divide-border">
                {sessions.map((s) => (
                  <li key={s.id}>
                    <button
                      type="button"
                      className={`w-full px-3 py-2 text-left text-xs transition-colors ${
                        selectedSessionId === s.id ? "bg-muted" : "hover:bg-muted/40"
                      }`}
                      onClick={() => {
                        setSelectedSessionId(s.id);
                        setSelectedRequestId(null);
                      }}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="truncate font-medium">
                          {s.title || `Session ${s.id.slice(0, 8)}`}
                        </span>
                        {s.status ? (
                          <Badge variant="outline" className="text-[10px]">
                            {s.status}
                          </Badge>
                        ) : null}
                      </div>
                      <div className="mt-1 flex items-center gap-1 text-[10px] text-muted-foreground">
                        <span className="truncate">
                          {agentNamesById.get(s.agent_id) ?? `agent:${s.agent_id.slice(0, 8)}`}
                        </span>
                        {s.runtime ? (
                          <Badge variant="outline" className="ml-auto text-[10px]">
                            {s.runtime}
                          </Badge>
                        ) : null}
                      </div>
                      <div className="mt-1 text-[10px] text-muted-foreground">
                        updated{" "}
                        {s.updated_at ? new Date(s.updated_at).toLocaleString() : "(unknown)"}
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {/* Session detail + requests + spans */}
        <div className="rounded-lg border border-border bg-card/50">
          {selectedSessionId ? (
            <SessionDetail
              key={selectedSessionId}
              sessionId={selectedSessionId}
              agentName={
                agentNamesById.get(
                  sessions.find((s) => s.id === selectedSessionId)?.agent_id ?? "",
                ) ?? null
              }
              selectedRequestId={selectedRequestId}
              onSelectRequest={setSelectedRequestId}
            />
          ) : (
            <EmptyState className="m-3">Pick a session on the left to see its requests.</EmptyState>
          )}
        </div>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// Session detail (right pane)
// ────────────────────────────────────────────────────────────────────

interface SessionDetailProps {
  sessionId: string;
  agentName: string | null;
  selectedRequestId: string | null;
  onSelectRequest: (id: string | null) => void;
}

function SessionDetail({
  sessionId,
  agentName,
  selectedRequestId,
  onSelectRequest,
}: SessionDetailProps) {
  const [detail, setDetail] = useState<ActivitySessionDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // selectedRequestId intentionally omitted from deps: the belongs-to-
  // session check should run only when the session changes, not on
  // every request click (which would race with parent state updates).
  // biome-ignore lint/correctness/useExhaustiveDependencies: load-once
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setDetail(null);
    // NOTE: deliberately do NOT call onSelectRequest(null) here — that
    // would wipe a deep-linked `?request=…` selection on first mount.
    // The session-list buttons already clear the request when the user
    // switches sessions interactively.
    api<ActivitySessionDetail>(`/activity/sessions/${sessionId}?include_requests=true`)
      .then((d) => {
        if (cancelled) return;
        setDetail(d);
        // After detail loads, drop the deep-linked request selection if
        // it doesn't actually belong to this session — saves the user
        // staring at a "spans not found" error.
        if (selectedRequestId && !(d.requests ?? []).some((r) => r.id === selectedRequestId)) {
          onSelectRequest(null);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [sessionId, onSelectRequest]);

  return (
    <div className="flex h-full flex-col">
      <div className="border-b border-border px-3 py-2">
        <div className="flex flex-wrap items-baseline gap-2">
          <span className="font-medium">{detail?.title || `Session ${sessionId.slice(0, 8)}`}</span>
          {agentName ? (
            <Badge variant="outline" className="text-[10px]">
              {agentName}
            </Badge>
          ) : null}
          {detail?.status ? (
            <Badge variant="outline" className="text-[10px]">
              {detail.status}
            </Badge>
          ) : null}
          <code className="ml-auto text-[10px] text-muted-foreground">{sessionId}</code>
        </div>
        {detail?.external_thread_key ? (
          <div className="mt-0.5 text-[11px] text-muted-foreground">
            ext: <code>{detail.external_thread_key}</code>
          </div>
        ) : null}
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        {loading ? (
          <div className="space-y-2 p-3">
            <Skeleton className="h-4 w-72" />
            <Skeleton className="h-4 w-64" />
          </div>
        ) : error ? (
          <ErrorBanner className="m-3">{error}</ErrorBanner>
        ) : detail?.requests && detail.requests.length > 0 ? (
          <div className="space-y-3 p-3">
            <ConversationPane sessionId={sessionId} />
            <RequestsTable
              requests={detail.requests}
              selectedId={selectedRequestId}
              onSelect={onSelectRequest}
            />
            {selectedRequestId ? (
              <SpanTree key={selectedRequestId} requestId={selectedRequestId} />
            ) : null}
          </div>
        ) : (
          <EmptyState className="m-3">No requests recorded for this session yet.</EmptyState>
        )}
      </div>
    </div>
  );
}

function RequestsTable({
  requests,
  selectedId,
  onSelect,
}: {
  requests: ActivityRequest[];
  selectedId: string | null;
  onSelect: (id: string | null) => void;
}) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Started</TableHead>
          <TableHead>Status</TableHead>
          <TableHead>Outcome</TableHead>
          <TableHead>Model</TableHead>
          <TableHead className="text-right">Latency</TableHead>
          <TableHead className="text-right">Tokens (in/out)</TableHead>
          <TableHead className="text-right">Cost</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {requests.map((r) => (
          <TableRow
            key={r.id}
            className={`cursor-pointer ${selectedId === r.id ? "bg-muted" : ""}`}
            onClick={() => onSelect(selectedId === r.id ? null : r.id)}
          >
            <TableCell className="font-mono text-[11px]">
              {r.started_at ? new Date(r.started_at).toLocaleTimeString() : "—"}
            </TableCell>
            <TableCell>
              <Badge variant="outline" className="text-[10px]">
                {r.status ?? "?"}
              </Badge>
            </TableCell>
            <TableCell>
              <Badge variant="outline" className="text-[10px]">
                {r.outcome ?? "?"}
              </Badge>
            </TableCell>
            <TableCell className="font-mono text-[11px]">
              {r.model ?? "—"}{" "}
              <span className="text-muted-foreground">{r.provider ? `· ${r.provider}` : ""}</span>
            </TableCell>
            <TableCell className="text-right font-mono text-[11px]">
              {r.latency_ms !== null ? `${r.latency_ms} ms` : "—"}
            </TableCell>
            <TableCell className="text-right font-mono text-[11px]">
              {r.input_tokens ?? 0}/{r.output_tokens ?? 0}
            </TableCell>
            <TableCell className="text-right font-mono text-[11px]">
              {r.cost_usd !== null ? `$${r.cost_usd.toFixed(4)}` : "—"}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

// ────────────────────────────────────────────────────────────────────
// Conversation pane — rebuilt user/assistant timeline
// ────────────────────────────────────────────────────────────────────
//
// Hits `/v1/activity/sessions/{id}/messages`, which the runtime
// reconstructs by joining `AgentRequest.input_text` (user side) with
// `ResponseRecord.final_text` (assistant side). Long messages are
// truncated by default with a "show full" toggle so the pane stays
// scannable for sessions with chunky outputs.

const COLLAPSED_PREVIEW_CHARS = 600;

function ConversationPane({ sessionId }: { sessionId: string }) {
  const [messages, setMessages] = useState<ActivityMessage[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setMessages(null);
    setExpanded(false);
    api<ActivityListEnvelope<ActivityMessage>>(`/activity/sessions/${sessionId}/messages?limit=200`)
      .then((d) => {
        if (!cancelled) setMessages(d.data ?? []);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  const visible = useMemo(() => {
    if (!messages) return [];
    if (expanded || messages.length <= 6) return messages;
    // Show first 2 and last 4 — typical "what was the prompt + how did
    // it end" debugging pattern.
    return [...messages.slice(0, 2), ...messages.slice(-4)];
  }, [messages, expanded]);

  return (
    <div className="rounded-md border border-border bg-background p-3">
      <div className="mb-2 flex items-center gap-2 text-xs">
        <span className="font-medium">Conversation</span>
        {messages && messages.length > 6 && !expanded ? (
          <button
            type="button"
            className="text-[11px] text-muted-foreground hover:text-foreground"
            onClick={() => setExpanded(true)}
          >
            show all {messages.length}
          </button>
        ) : null}
        {messages ? (
          <span className="ml-auto font-mono text-[10px] text-muted-foreground">
            {messages.length} msg
          </span>
        ) : null}
      </div>
      {loading ? (
        <Skeleton className="h-12 w-full" />
      ) : error ? (
        <ErrorBanner>{error}</ErrorBanner>
      ) : !messages || messages.length === 0 ? (
        <EmptyState>No messages reconstructed for this session.</EmptyState>
      ) : (
        <ul className="space-y-2 text-xs">
          {visible.map((m, idx) => (
            // The runtime guarantees ordering matches insertion order;
            // when collapsed we still want stable keys for the visible
            // window so React doesn't thrash the DOM.
            <li
              key={`${m.response_id ?? "user"}-${idx}`}
              className={`rounded-md border px-2 py-1.5 ${
                m.role === "user"
                  ? "border-border/60 bg-muted/30"
                  : "border-primary/20 bg-primary/5"
              }`}
            >
              <div className="mb-1 flex items-center gap-1.5">
                <Badge variant="outline" className="text-[9px] uppercase tracking-[0.1em]">
                  {m.role}
                </Badge>
                {m.response_id ? (
                  <code className="text-[10px] text-muted-foreground">{m.response_id}</code>
                ) : null}
              </div>
              <p className="whitespace-pre-wrap font-mono text-[11px] leading-relaxed">
                {m.content.length > COLLAPSED_PREVIEW_CHARS && !expanded
                  ? `${m.content.slice(0, COLLAPSED_PREVIEW_CHARS)}…`
                  : m.content}
              </p>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// Span tree
// ────────────────────────────────────────────────────────────────────

function SpanTree({ requestId }: { requestId: string }) {
  const [spans, setSpans] = useState<ActivitySpan[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedSpanId, setSelectedSpanId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setSpans(null);
    setSelectedSpanId(null);
    api<ActivityListEnvelope<ActivitySpan>>(`/activity/requests/${requestId}/spans`)
      .then((d) => {
        if (!cancelled) setSpans(d.data ?? []);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [requestId]);

  // Build parent → children index for the tree.
  const childrenById = useMemo(() => {
    const m = new Map<string | null, ActivitySpan[]>();
    if (!spans) return m;
    for (const s of spans) {
      const key = s.parent_span_id ?? null;
      const arr = m.get(key) ?? [];
      arr.push(s);
      m.set(key, arr);
    }
    return m;
  }, [spans]);

  const selectedSpan = useMemo(
    () => spans?.find((s) => s.id === selectedSpanId) ?? null,
    [spans, selectedSpanId],
  );

  return (
    <div className="rounded-md border border-border bg-background p-3">
      <div className="mb-2 flex items-center gap-2 text-xs">
        <span className="font-medium">Trace spans</span>
        <code className="text-muted-foreground">request {requestId.slice(0, 8)}</code>
        {selectedSpan ? (
          <button
            type="button"
            className="ml-auto text-[11px] text-muted-foreground hover:text-foreground"
            onClick={() => setSelectedSpanId(null)}
          >
            close detail
          </button>
        ) : null}
      </div>
      {loading ? (
        <Skeleton className="h-12 w-full" />
      ) : error ? (
        <ErrorBanner>{error}</ErrorBanner>
      ) : !spans || spans.length === 0 ? (
        <EmptyState>No spans recorded for this request.</EmptyState>
      ) : (
        <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
          <ul className="space-y-0.5 text-xs">
            {(childrenById.get(null) ?? []).map((root) => (
              <SpanRow
                key={root.id}
                span={root}
                childrenById={childrenById}
                depth={0}
                selectedSpanId={selectedSpanId}
                onSelect={setSelectedSpanId}
              />
            ))}
          </ul>
          {selectedSpan ? (
            <SpanDetail span={selectedSpan} />
          ) : (
            <div className="hidden rounded-md border border-dashed border-border/60 px-3 py-4 text-center text-[11px] text-muted-foreground lg:block">
              Click a span to inspect its attributes, events, and any recorded error.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function SpanRow({
  span,
  childrenById,
  depth,
  selectedSpanId,
  onSelect,
}: {
  span: ActivitySpan;
  childrenById: Map<string | null, ActivitySpan[]>;
  depth: number;
  selectedSpanId: string | null;
  onSelect: (id: string | null) => void;
}) {
  const [open, setOpen] = useState(true);
  const kids = childrenById.get(span.id) ?? [];
  const hasKids = kids.length > 0;
  const isSelected = selectedSpanId === span.id;
  return (
    <li>
      <div
        className={`flex items-center gap-1 rounded px-1 py-0.5 transition-colors ${
          isSelected ? "bg-muted" : "hover:bg-muted/40"
        }`}
        style={{ paddingLeft: `${depth * 14 + 4}px` }}
      >
        {hasKids ? (
          <button
            type="button"
            className="text-muted-foreground"
            onClick={(event) => {
              event.stopPropagation();
              setOpen((p) => !p);
            }}
            aria-label={open ? "Collapse" : "Expand"}
          >
            {open ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
          </button>
        ) : (
          <span className="inline-block w-3" />
        )}
        <button
          type="button"
          className="flex min-w-0 flex-1 items-center gap-1 text-left"
          onClick={() => onSelect(isSelected ? null : span.id)}
        >
          <Badge variant="outline" className="text-[10px]">
            {span.kind ?? "span"}
          </Badge>
          <span className="truncate font-mono">{span.name ?? "(unnamed)"}</span>
        </button>
        <span className="font-mono text-[10px] text-muted-foreground">
          {span.duration_ms !== null ? `${span.duration_ms} ms` : "—"}
        </span>
        {span.status && span.status !== "ok" ? (
          <Badge variant="outline" className="text-[10px]">
            {span.status}
          </Badge>
        ) : null}
        {span.ab_variant ? (
          <Badge variant="outline" className="text-[10px]">
            ab:{span.ab_variant}
          </Badge>
        ) : null}
      </div>
      {open && hasKids ? (
        <ul className="space-y-0.5">
          {kids.map((k) => (
            <SpanRow
              key={k.id}
              span={k}
              childrenById={childrenById}
              depth={depth + 1}
              selectedSpanId={selectedSpanId}
              onSelect={onSelect}
            />
          ))}
        </ul>
      ) : null}
    </li>
  );
}

function SpanDetail({ span }: { span: ActivitySpan }) {
  const attrEntries = useMemo(
    () => Object.entries(span.attributes ?? {}).sort((a, b) => a[0].localeCompare(b[0])),
    [span.attributes],
  );
  return (
    <div className="rounded-md border border-border/70 bg-card/40 p-2 text-xs">
      <div className="mb-2 flex items-center gap-1.5">
        <Badge variant="outline" className="text-[10px]">
          {span.kind ?? "span"}
        </Badge>
        <span className="truncate font-mono font-medium">{span.name ?? "(unnamed)"}</span>
        <span className="ml-auto font-mono text-[10px] text-muted-foreground">
          {span.duration_ms !== null ? `${span.duration_ms} ms` : "—"}
        </span>
      </div>
      <dl className="grid grid-cols-[auto_1fr] gap-x-2 gap-y-0.5 text-[11px]">
        <dt className="text-muted-foreground">id</dt>
        <dd className="font-mono break-all">{span.id}</dd>
        {span.sdk_span_id ? (
          <>
            <dt className="text-muted-foreground">sdk_span_id</dt>
            <dd className="font-mono break-all">{span.sdk_span_id}</dd>
          </>
        ) : null}
        {span.parent_span_id ? (
          <>
            <dt className="text-muted-foreground">parent</dt>
            <dd className="font-mono break-all">{span.parent_span_id}</dd>
          </>
        ) : null}
        {span.started_at ? (
          <>
            <dt className="text-muted-foreground">started</dt>
            <dd className="font-mono">{new Date(span.started_at).toLocaleTimeString()}</dd>
          </>
        ) : null}
      </dl>
      {attrEntries.length > 0 ? (
        <details className="mt-2" open>
          <summary className="cursor-pointer text-[11px] font-medium text-muted-foreground hover:text-foreground">
            attributes ({attrEntries.length})
          </summary>
          <pre className="mt-1 max-h-64 overflow-auto rounded bg-muted/30 p-2 font-mono text-[10px] leading-relaxed">
            {JSON.stringify(Object.fromEntries(attrEntries), null, 2)}
          </pre>
        </details>
      ) : null}
      {span.events && span.events.length > 0 ? (
        <details className="mt-2">
          <summary className="cursor-pointer text-[11px] font-medium text-muted-foreground hover:text-foreground">
            events ({span.events.length})
          </summary>
          <pre className="mt-1 max-h-48 overflow-auto rounded bg-muted/30 p-2 font-mono text-[10px] leading-relaxed">
            {JSON.stringify(span.events, null, 2)}
          </pre>
        </details>
      ) : null}
      {span.error ? (
        <details className="mt-2" open>
          <summary className="cursor-pointer text-[11px] font-medium text-destructive">
            error
          </summary>
          <pre className="mt-1 max-h-48 overflow-auto rounded border border-destructive/40 bg-destructive/5 p-2 font-mono text-[10px] leading-relaxed text-destructive">
            {JSON.stringify(span.error, null, 2)}
          </pre>
        </details>
      ) : null}
    </div>
  );
}
